import React, {
  createContext,
  useState,
  PropsWithChildren,
  useContext,
  useCallback,
} from "react";
import { SetState, SetStateArg, useStateWithRef } from "../hooks";

export interface ILocalStorageContext {
  getKeys: () => string[];
  getString: (key: string, fallback?: string) => string | typeof fallback;
  setString: (key: string, value: string) => void;
  getJson: <T>(key: string, fallback: T) => T | typeof fallback;
  setJson: <T>(key: string, value: T) => void;
  delValue: (key: string) => void;
}

export type IStore = { [key: string]: any };

export const LocalStorageContext = createContext<ILocalStorageContext>({
  getKeys: () => [],
  getString: (key: string, fallback?: string) => fallback,
  setString: (key: string, value: string) => null,
  getJson: <T,>(key: string, fallback: T) => fallback,
  setJson: <T,>(key: string, value: T) => null,
  delValue: (key: string) => null,
});
export const LocalStorageProvider: React.FC<PropsWithChildren> = (props) => {
  const [store, setStore] = useStateWithRef<IStore>({ ...localStorage });

  const getString = (key: string, fallback?: string): string | undefined => {
    if (key in store) {
      return store[key];
    }
    const v = localStorage.getItem(key);
    if (v === null && fallback) {
      setString(key, fallback);
    }
    return localStorage.getItem(key) || undefined;
  };

  const getKeys = useCallback(() => {
    const keys: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key) {
        keys.push(key);
      }
    }
    return keys;
  }, [])

  const setString = (key: string, value: string) => {
    localStorage.setItem(key, value);
    setStore({ ...localStorage });
  };

  const delValue = (key: string) => {
    localStorage.removeItem(key);
    setStore((v) => {
      delete v[key];
      return v;
    });
  };

  const getJson = <TValue,>(
    key: string,
    fallback?: any
  ): TValue | typeof fallback => {
    const v = getString(key);
    if (!v) {
      if (!fallback) {
        setJson(key, fallback);
      }
      return fallback;
    }
    return JSON.parse(v);
  };

  const setJson = <TValue,>(key: string, value: TValue) => {
    setString(key, JSON.stringify(value));
  };

  return (
    <LocalStorageContext.Provider
      value={{ getString, setString, delValue, getJson, setJson, getKeys }}
    >
      {props.children}
    </LocalStorageContext.Provider>
  );
};

export const useStoredJson = <T,>(
  key: string,
  initial: T
): [T | typeof initial, SetState<T>, React.MutableRefObject<T>] => {
  const { getJson, setJson } = useContext(LocalStorageContext);
  const [state, setState, ref] = useStateWithRef(getJson(key, initial));
  const handleSetValue = (v: SetStateArg<T>) => {
    if (v instanceof Function) {
      const _v = v(ref.current);
      setState(_v);
      setJson(key, _v);
    } else {
      setState(v);
      setJson(key, v);
    }
  };
  return [state, handleSetValue, ref];
};
